import type {
	CAInputAppearance,
	CAInputHTMLType,
	CAInputMessage,
	CAInputStatus,
	CAInputType,
	CALabelAppearance,
} from '@ds/interfaces'
import { DELAY } from '@vuejs/helpers/_constants'
import { debounce } from '@vuejs/utils/globalUtils'
import { defineComponent, type PropType } from 'vue'

import CAIcon from '../shared/CAIcon'

/**
 * The `CAInput` component is used to create an input field specifically for text values.
 * It supports various features such as validation, custom styling, and dynamic icons.
 *
 * @component CAInput
 *
 * @prop {string} [additionalInputClasses=''] - Additional classes for the input element.
 * @prop {string} [additionalWrapperClasses=''] - Additional classes for the wrapper div
 * @prop {CAInputAppearance} [appearance='bordered'] - The appearance style of the input.
 * @prop {boolean} [autofocus=false] - Whether the input should be focused automatically on page load.
 * @prop {boolean} [buttonGroup=false] - Whether the input should be displayed as a button group.
 * @prop {{ max?: number; min?: number }} [characterCount] - Character count limits for the input.
 * @prop {string} [dataTest='ca-input-text'] - Data attribute for testing.
 * @prop {boolean} [disabled=false] - Whether the input is disabled.
 * @prop {string} [icon=''] - The icon to be displayed on the left side of the input.
 * @prop {boolean} [iconHoverEffect=false] - Whether there is an hover effect on the icon.
 * @prop {string} [iconRight=''] - The icon to be displayed on the right side of the input.
 * @prop {string} [id=''] - The ID for the input element.
 * @prop {CAInputHTMLType} [inputMode='text'] - The input mode for the input element.
 * @prop {string} [inputRef=''] - The reference name for the input element.
 * @prop {string} [label=''] - The label for the input element.
 * @prop {CALabelAppearance} [labelAppearance='hidden'] - The appearance of the label.
 * @prop {string} [labelDataTest='ca-input-number-label'] - The data-test attribute for the label.
 * @prop {CAInputMessage | null} [message=null] - The message to be displayed below the input.
 * @prop {string} [modelValue=''] - The value of the input.
 * @prop {string} [pattern=''] - The pattern allowed for the input.
 * @prop {string} [placeholder=''] - The placeholder text for the input.
 * @prop {boolean} [required=false] - Whether the input is required.
 * @prop {CAInputStatus} [status=''] - The status of the input.
 * @prop {boolean} [sustainLabel=false] - Whether the label should be sustained.
 * @prop {string} [title=''] - The title for the input element.
 * @prop {CAInputType} [type='text'] - The type of the input element.
 * @prop {string[]} [validKeys] - The valid keys for the input element.
 *
 * @emit blur - When the input loses focus.
 * @emit enter - When the Enter key is pressed.
 * @emit escape - When the Escape key is pressed.
 * @emit focus - When the input gains focus.
 * @emit iconClick - When the left icon is clicked.
 * @emit iconRightClick - When the right icon is clicked.
 * @emit message - When the input message changes.
 * @emit update:modelValue - When the input value changes.
 */
export default defineComponent({
	name: 'CAInput',

	props: {
		/**
		 * Additional classes to be added to the input element.
		 * @default ''
		 * @type {string}
		 */
		additionalInputClasses: { default: '', type: String },
		/**
		 * Additional classes to be added to the wrapper div.
		 * @default ''
		 * @type {string}
		 */
		additionalWrapperClasses: { default: '', type: String },
		/**
		 * The appearance style of the input element.
		 * @default 'bordered'
		 * @type {CAInputAppearance}
		 * @enum { 'bordered', 'dynamic-icon', 'dynamic-icon-right', 'underlined' }
		 */
		appearance: { default: 'bordered', type: String as PropType<CAInputAppearance> },
		/**
		 * Whether the input should be focused automatically on page load.
		 * @default false
		 * @type {boolean}
		 */
		autofocus: { default: false, type: Boolean },
		/**
		 * Whether the input should be displayed as a button group.
		 * @default false
		 * @type {boolean}
		 */
		buttonGroup: { default: false, type: Boolean },
		/**
		 * Character count limits for the input.
		 * @optional
		 * @type {{ max?: number; min?: number }}
		 */
		characterCount: {
			optional: true,
			type: Object as PropType<{ max?: number; min?: number }>,
		},
		/**
		 * Data attribute for testing.
		 * @default 'ca-input-text'
		 * @type {string}
		 */
		dataTest: { default: 'ca-input-text', type: String },
		/**
		 * Whether the input is disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * The icon to be displayed on the left side of the input.
		 * @default ''
		 * @type {string}
		 */
		icon: { default: '', type: String },
		/**
		 * Whether there is an hover effect on the icon
		 * @default false
		 * @type {boolean}
		 */
		iconHoverEffect: { default: false, type: Boolean },
		/**
		 * The icon to be displayed on the right side of the input.
		 * @default ''
		 * @type {string}
		 */
		iconRight: { default: '', type: String },
		/**
		 * The ID for the input element.
		 * @default ''
		 * @type {string}
		 */
		id: { default: '', type: String },
		/**
		 * The input mode for the input element.
		 * @default 'text'
		 * @type {CAInputHTMLType}
		 * @enum { "text" | "decimal" | "email" | "none" | "numeric" | "search" | "tel" | "url" }
		 */
		inputMode: { default: 'text', type: String as PropType<CAInputHTMLType> },
		/**
		 * The reference name for the input element.
		 * @default ''
		 * @type {string}
		 */
		inputRef: { default: '', type: String },
		/**
		 * The label for the input element.
		 * @default ''
		 * @type {string}
		 */
		label: { default: '', type: String },
		/**
		 * The appearance of the label.
		 * @default 'hidden'
		 * @type {CALabelAppearance}
		 * @enum { 'hidden', 'static', 'grid' }
		 */
		labelAppearance: {
			default: 'hidden',
			type: String as PropType<CALabelAppearance>,
		},
		/**
		 * The data-test attribute for the label.
		 * @default 'ca-input-number-label'
		 * @type {string}
		 */
		labelDataTest: { default: 'ca-input-number-label', type: String },
		/**
		 * The message to be displayed below the input.
		 * @optional
		 * @type {CAInputMessage | null}
		 */
		message: { default: null, type: Object as PropType<CAInputMessage | null> },
		modelValue: { default: '', type: String },
		/**
		 * The pattern allowed for the input.
		 * @default ''
		 * @type {string}
		 */
		pattern: { default: '', type: String },
		/**
		 * The placeholder text for the input.
		 * @default ''
		 * @type {string}
		 */
		placeholder: { default: '', type: String },
		/**
		 * Whether the input is required.
		 * @default false
		 * @type {boolean}
		 */
		required: { default: false, type: Boolean },
		/**
		 * The status of the input.
		 * @default ''
		 * @type {CAInputStatus}
		 * @enum { 'error', 'success', '' }
		 */
		status: {
			default: '',
			type: String as PropType<CAInputStatus>,
		},
		/**
		 * Whether the label should be sustained.
		 * @default false
		 * @type {boolean}
		 */
		sustainLabel: { default: false, type: Boolean },
		/**
		 * The title for the input element.
		 * @default ''
		 * @type {string}
		 */
		title: { default: '', type: String },
		/**
		 * The type of the input element.
		 * @default 'number'
		 * @type {'number' | 'range' | 'tel'}
		 */
		type: {
			default: 'text',
			type: String as PropType<CAInputType>,
		},
		/**
		 * The valid keys for the input element.
		 * @optional
		 * @type {string[]}
		 */
		validKeys: { optional: true, type: Array as PropType<string[]> },
	},

	emits: {
		'update:modelValue': (_value: string) => true,
		blur: () => true,
		enter: (key: KeyboardEvent) => !!key,
		escape: (key: KeyboardEvent) => !!key,
		focus: () => true,
		iconClick: () => true,
		iconRightClick: () => true,
		message: (_message?: CAInputMessage) => true,
	},

	data(): Data {
		return {
			currentValue: this.modelValue,
			emitValue: debounce(() => this.$emit('update:modelValue', this.currentValue), DELAY.HALF_SECOND),
			expandedDynamicIcon: false,
			inputElement: null,
			isFocused: false,
		}
	},

	computed: {
		engageClearButton(): boolean {
			return this.showDynamicActions || this.isSearchType
		},

		inputClasses() {
			return {
				'ca-input': true,
				[this.additionalInputClasses]: true,
				'ca-input__button-group': this.buttonGroup,
				'ca-input__dynamic-icon': this.usingDynamicIconLayout,
				'ca-input__dynamic-icon-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon',
				'ca-input__dynamic-icon__right-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon-right',
				'ca-input__error': this.status === 'error' || this.message?.type === 'error',
				'ca-input__grid': this.labelAppearance === 'grid',
				'ca-input__success': this.status === 'success',
				'ca-input__sustain': this.sustainLabel,
				'ca-input__touched': this.modelValue ?? !!this.modelValue,
				'ca-input__underline': this.appearance === 'underlined',
				'ca-input__with-icon': !!this.icon || !!this.iconRight,
				'ca-input__with-icon--both': !!this.icon && !!this.iconRight,
				'ca-input__with-icon--right': !!this.iconRight,
			}
		},

		inputWrapperClasses() {
			return {
				'ca-form-control': true,
				[this.additionalWrapperClasses]: true,
				'ca-form-control__dynamic-icon': this.appearance === 'dynamic-icon',
				'ca-form-control__dynamic-icon ca-form-control__dynamic-icon__right': this.appearance === 'dynamic-icon-right',
				'ca-form-control__dynamic-icon ca-form-control__dynamic-icon__right-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon-right',
				'ca-form-control__grid': this.labelAppearance === 'grid',
				'ca-form-control__required': this.required,
				'ca-input--focused': this.isFocused,
			}
		},

		isSearchType(): boolean {
			return this.type === 'search'
		},

		labelClasses() {
			return {
				'ca-label': true,
				'ca-label__button-group': this.buttonGroup,
				'ca-label__dynamic-icon': this.usingDynamicIconLayout,
				'ca-label__dynamic-icon-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon',
				'ca-label__dynamic-icon__right': this.appearance === 'dynamic-icon-right',
				'ca-label__dynamic-icon__right-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon-right',
				'ca-label__error': this.status === 'error' || this.message?.type === 'error',
				'ca-label__grid': this.labelAppearance === 'grid',
				'ca-label__static': this.labelAppearance === 'static',
				'ca-label__success': this.status === 'success',
				'ca-label__sustain': this.sustainLabel,
				'ca-label__touched': this.modelValue ?? !!this.modelValue,
				'ca-label__underline': this.appearance === 'underlined',
				'ca-label__with-icon': this.icon !== '',
			}
		},

		labelWrapperClasses() {
			return {
				'ca-input__title-label': true,
				'ca-input__title-label--dynamic-icon': this.appearance === 'dynamic-icon',
				'ca-input__title-label--dynamic-icon-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon',
				'ca-input__title-label--dynamic-icon__right': this.appearance === 'dynamic-icon-right',
				'ca-input__title-label--dynamic-icon__right-expanded': this.expandedDynamicIcon && this.appearance === 'dynamic-icon-right',
				'ca-input__title-label--grid': this.labelAppearance === 'grid',
			}
		},

		showClearButton(): boolean {
			return this.engageClearButton && this.currentValue?.toString().trim() !== ''
		},

		showDynamicActions(): boolean {
			return this.usingDynamicIconLayout && this.expandedDynamicIcon
		},

		showPlaceholderRequired(): boolean {
			return !!this.placeholder && this.required && !this.label
		},

		showRequired(): boolean {
			return this.required && !!this.label
		},

		usingDynamicIconLayout(): boolean {
			return this.appearance === 'dynamic-icon' || this.appearance === 'dynamic-icon-right'
		},
	},

	beforeUnmount() {
		if (this.inputElement) {
			this.inputElement.removeEventListener('focus', this.focus)
			this.inputElement.removeEventListener('blur', this.handleBlur)
		}
	},

	watch: {
		expandedDynamicIcon(newVal) {
			if (newVal === true) {
				this.$nextTick(() => {
					this.focus()
				})
			}
		},

		modelValue(newVal) {
			this.currentValue = newVal
		},
	},

	mounted() {
		this.inputElement = this.$refs[this.inputRef] as HTMLInputElement

		if (this.inputElement) {
			this.inputElement.addEventListener('focus', this.focus)
			this.inputElement.addEventListener('blur', this.handleBlur)
		}

		if (this.autofocus) {
			this.focus()
		}
	},

	methods: {
		blur() {
			if (this.inputElement) {
				this.isFocused = false
				this.$emit('blur')
			}
		},

		expandToggle(event: MouseEvent) {
			const isInput = event.target === this.$refs[this.inputRef]

			if (isInput && this.expandedDynamicIcon) return

			if (!this.usingDynamicIconLayout) return
			this.expandedDynamicIcon = !this.expandedDynamicIcon

			if (this.expandedDynamicIcon) {
				this.$nextTick(() => {
					this.focus()
				})
			}
		},

		focus() {
			if (this.inputElement) {
				this.isFocused = true
				this.$emit('focus')
			}
		},

		handleBlur() {
			this.blur()

			if (this.currentValue?.toString().trim() === '') {
				this.expandedDynamicIcon = false
			}
		},

		onHandleKeydown(keyEvent: KeyboardEvent) {
			if (keyEvent.ctrlKey || keyEvent.metaKey) return

			if (!!this.validKeys && !this.validKeys?.includes(keyEvent.key)) {
				keyEvent.preventDefault()
			}

			switch (keyEvent.key) {
				case 'Escape':
					this.$emit('escape', keyEvent)
					break

				case 'Enter':
					this.$emit('enter', keyEvent)
					break
			}
		},

		onInputChange(event: Event) {
			const inputValue = (event.target as HTMLInputElement).value

			this.$emit('message', {
				text: '',
				type: 'success',
			})

			this.currentValue = inputValue

			this.emitValue()
		},

		reset() {
			const inputRef = this.$refs[this.inputRef] as HTMLInputElement

			this.currentValue = ''
			this.emitValue()

			if (inputRef) {
				inputRef.value = ''
			}
		},
	},

	render() {
		const iconInputClasses = {
			'ca-input__icon-input': true,
			'ca-input-wrapper__error': this.status === 'error' || this.message?.type === 'error',
			'ca-input-wrapper__success': this.status === 'success',
			'ca-input__icon-input--dynamic-icon__right': this.appearance === 'dynamic-icon-right',
			'ca-input__icon-input--dynamic-icon': this.appearance === 'dynamic-icon',
			'ca-input__icon-input--dynamic-icon__right-expanded': this.appearance === 'dynamic-icon-right' && this.expandedDynamicIcon,
			'ca-input__icon-input--dynamic-icon-expanded': this.appearance === 'dynamic-icon' && this.expandedDynamicIcon,
		}

		return (
			<div
				aria-labelledby={`${this.id}-title`}
				class={this.inputWrapperClasses}
				role="group"
			>
				{this.title &&
					<p
						class="ca-input__title"
						data-test="ca-input-title"
						id={`${this.id}-title`}
					>
						{this.title}
					</p>
				}

				{this.label &&
					<div class={this.labelWrapperClasses}>
						<label
							class={this.labelClasses}
							data-test={this.labelDataTest}
							for={this.id}
							id={`${this.id}-label`}
						>
							{this.label} {this.showRequired &&
								<span aria-hidden="true" class="ca-required-text">*</span>
							}
						</label>
					</div>
				}

				<div class={iconInputClasses} onClick={this.expandToggle}>
					{this.icon &&
						<div class="w-auto h-auto" onClick={() => this.$emit('iconClick')}>
							<CAIcon
								class={{
									'ca-input__icon': true,
									'ca-input__icon--left': true,
									'ca-input__icon--left__dynamic-icon': this.appearance === 'dynamic-icon',
									'ca-input__icon--left__dynamic-icon__right': this.appearance === 'dynamic-icon-right',
									'ca-input__icon--clickable': this.iconHoverEffect,
								}}
								icon={this.icon}
								svg-class="w-3 h-3"
							/>
						</div>
					}

					<input
						ref={this.inputRef}
						aria-describedby={`${this.id}-message`}
						aria-invalid={!!this.message && this.message.type === 'error'}
						aria-labelledby={`${this.id}-label`}
						aria-required={this.required}
						autofocus={!!this.autofocus ? this.autofocus : undefined}
						class={this.inputClasses}
						data-test={this.dataTest}
						data-test-component="ca-input"
						disabled={this.disabled}
						id={this.id}
						inputmode={this.inputMode}
						maxlength={this.characterCount?.max}
						minlength={this.characterCount?.min}
						pattern={this.pattern}
						placeholder={`${this.placeholder}${this.showPlaceholderRequired ? ' *' : ''}`}
						required={this.required}
						type={this.type}
						v-model={this.currentValue}
						onBlur={this.handleBlur}
						onFocus={() => this.$emit('focus')}
						onInput={this.onInputChange}
						onKeydown={this.onHandleKeydown}
					/>

					{this.$slots.default?.()}

					{this.iconRight &&
						<div onClick={() => this.$emit('iconRightClick')}>
							<CAIcon
								class={{
									'ca-input__icon': true,
									'ca-input__icon--right': true,
									'ca-input__icon--right__dynamic-icon': this.appearance === 'dynamic-icon',
									'ca-input__icon--right__dynamic-icon__right': this.appearance === 'dynamic-icon-right',
									'ca-input__icon--clickable': this.iconHoverEffect,
								}}
								icon={this.iconRight}
								svg-class="w-3 h-3"
							/>
						</div>
					}

					{this.showClearButton &&
						<div onClick={this.reset}>
							<CAIcon
								class={{
									'ca-input__icon': true,
									'ca-input__icon--right': true,
									'ca-input__icon--right__dynamic-icon': this.appearance === 'dynamic-icon',
									'ca-input__icon--right__dynamic-icon__right': this.appearance === 'dynamic-icon-right',
									'ca-input__icon--clickable': this.iconHoverEffect,
									'cursor-pointer text-gray-550 hover:text-gray-500': true,
								}}
								icon="SVGClose"
								svg-class="w-3 h-3"
							/>
						</div>
					}
				</div>

				{this.message && this.message.text &&
					<p
						class={`ca-input__message ca-input__message--${this.message.type}`}
						data-test="ca-input-message"
						id={`${this.id}-message`}
						role={this.message.type === 'error' ? 'alert' : 'status'}
					>
						{this.message.text}
					</p>
				}
			</div>
		)
	},
})

interface Data {
	currentValue: string
	emitValue: () => void
	expandedDynamicIcon: boolean
	inputElement: HTMLInputElement | null
	isFocused: boolean
}
